import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import { ReactNode } from "react";
import BaseField, { IProps as IBaseFieldProps, IState as IBaseFieldState } from "../BaseField";
import classes from "./classes.module.scss";
import classnames from "classnames";
import { XMarkIcon, Square2StackIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { ToasterService } from "@Front/Components/DesignSystem/Toaster";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";


export type IProps = IBaseFieldProps & {
	canCopy?: boolean;
	password?: boolean;
};

type IState = IBaseFieldState & {
	showPassword: boolean;
};

export default class TextField extends BaseField<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			...this.getDefaultState(),
			showPassword: false,
		};
	}

	private getInputType(): "text" | "password" {
		return this.props.password && !this.state.showPassword ? "password" : "text";
	}

	private getAutoComplete(): string | undefined {
		if (!this.props.password) return undefined;
		return this.props.name === "password_confirm" ? "new-password" : "current-password";
	}

	private renderInputTrailingIcons(): ReactNode {
		if (this.props.password && !this.hasError()) {
			return (
				<button
					type="button"
					className={classes["password-toggle-icon"]}
					onClick={this.togglePasswordVisibility}
					aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.formFieldTogglePasswordVisibility}>
					{this.state.showPassword ? (
						<EyeSlashIcon height="24px" width="24px" color={"var(--color-neutral-700)"} />
					) : (
						<EyeIcon height="24px" width="24px" color={"var(--color-neutral-700)"} />
					)}
				</button>
			);
		}
		if (this.props.canCopy && !this.hasError() && !this.props.password) {
			return (
				<button
					type="button"
					className={classes["copy-icon"]}
					onClick={this.onCopyClick}
					aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.formFieldCopyValue}>
					<Square2StackIcon height="24px" width="24px" color={"var(--color-neutral-700)"} />
				</button>
			);
		}
		if (this.hasError()) {
			return (
				<button
					type="button"
					className={classes["cross-icon"]}
					onClick={this.onCopyClick}
					aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.formFieldCopyValue}>
					<XMarkIcon height="24px" width="24px" color="var(--input-error)" />
				</button>
			);
		}
		return null;
	}

	public override render(): ReactNode {
		const value = this.state.value ?? "";
		return (
			<div className={classes["root"]} data-is-disabled={this.props.disabled}>
				<label>
					{this.props.label && (
						<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.INPUT_LABEL} className={classes["label"]}>
							{this.props.label}
						</Typography>
					)}
					<div className={classes["input-container"]} data-value={value} data-is-errored={this.hasError().toString()}>
						<input
							onChange={this.onChange}
							className={classnames(classes["input"], this.props.className)}
							placeholder={this.props.placeholder}
							value={value}
							onFocus={this.onFocus}
							onBlur={this.onBlur}
							name={this.props.name}
							disabled={this.props.disabled}
							readOnly={this.props.readonly}
							type={this.getInputType()}
							autoComplete={this.getAutoComplete()}
						/>
						{this.renderInputTrailingIcons()}
					</div>
				</label>
				{this.hasError() && <div className={classes["errors-container"]}>{this.renderErrors()}</div>}
			</div>
		);
	}

	private onCopyClick = (): void => {
		if (this.props.canCopy) {
			navigator.clipboard.writeText(this.state.value ?? "");
			ToasterService.getInstance().success({ title: "Copié avec succès !", description: this.state.value });
		}
	};

	private togglePasswordVisibility = (): void => {
		this.setState({ showPassword: !this.state.showPassword });
	};
}
